import React, { useRef } from "react";
import { FiDownload, FiEye, FiEyeOff, FiStar, FiTrash2, FiUpload } from "react-icons/fi";
import type { KeyPersonnelResume, ResumeFileInput } from "../types/KeyPersonnel.type";
import Tooltip from "components/Tooltip";
import { formatFileSize } from "newui/features/Rfx/Evaluation/components/steps/RevisionsStep/utils/format-file-size";

interface ResumeTableProps {
	isResumePrivate: boolean;
	resumeFiles: ResumeFileInput[];
	resumes: KeyPersonnelResume[];
	onAdd: (file: File) => void;
	onChangePrivate: () => void;
	onRemove: (index: number) => void;
	onSetDefault: (index: number) => void;
}

interface DisplayResume {
	name: string;
	downloadUrl: string | null;
	isDefault: boolean;
	isNew: boolean;
	isResumePrivate?: boolean;
	size: string;
	type: string;
}

const buildDisplayList = (
	resumes: KeyPersonnelResume[],
	resumeFiles: ResumeFileInput[],
	isResumePrivate: boolean
): DisplayResume[] => {
	return resumeFiles
		.filter((rf) => !rf.is_remove)
		.map((rf) => {
			if (rf.id) {
				const existing = resumes.find((r) => r.id === rf.id);
				return {
					name: existing?.file_name ?? "Unknown",
					size: existing ? formatFileSize(existing.file_size) : "-",
					type: existing?.file_type ?? "-",
					isDefault: rf.is_default,
					isResumePrivate,
					isNew: false,
					downloadUrl: existing?.file_path ?? null,
				};
			}
			return {
				name: rf.file?.name ?? "New file",
				size: rf.file ? formatFileSize(rf.file.size) : "-",
				type: rf.file?.name.split(".").pop()?.toUpperCase() ?? "-",
				isDefault: rf.is_default,
				isResumePrivate,
				isNew: true,
				downloadUrl: null,
			};
		});
};

const COLUMN_HEADER_STYLE = "px-4 py-3 text-gray-600";

export const ResumeTable: React.FC<ResumeTableProps> = ({
	isResumePrivate,
	resumes,
	resumeFiles,
	onAdd,
	onChangePrivate,
	onSetDefault,
	onRemove,
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);

	const displayList = buildDisplayList(resumes, resumeFiles, isResumePrivate);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			onAdd(file);
		}
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	return (
		<div className="flex flex-col" data-testid="resume-table">
			<div className="overflow-x-auto rounded-lg border border-gray-200">
				<table className="w-full text-left text-sm">
					<thead className="border-b border-gray-200 bg-gray-50">
						<tr>
							<th className={COLUMN_HEADER_STYLE}>Default</th>
							<th className={COLUMN_HEADER_STYLE}>Public/Private</th>
							<th className={COLUMN_HEADER_STYLE}>File Name</th>
							<th className={COLUMN_HEADER_STYLE}>Size</th>
							<th className={COLUMN_HEADER_STYLE}>Type</th>
							<th className={COLUMN_HEADER_STYLE}></th>
						</tr>
					</thead>
					<tbody>
						{displayList.length === 0 ? (
							<tr>
								<td
									colSpan={5}
									className="px-4 py-8 text-center text-gray-400"
									data-testid="empty-resumes"
								>
									No resumes uploaded
								</td>
							</tr>
						) : (
							displayList.map((item, index) => (
								<tr
									key={index}
									className="border-b border-gray-100 last:border-b-0"
									data-testid={`resume-row-${index}`}
								>
									<td className="px-4 py-3">
										{item.isDefault && (
											<div className="pl-2">
												<FiStar
													className="fill-yellow-400 text-yellow-400"
													size={16}
													data-testid={`default-star-${index}`}
												/>
											</div>
										)}
										{!item.isDefault && (
											<button
												type="button"
												onClick={() => onSetDefault(index)}
												className="rounded pl-2 text-gray-400 hover:bg-yellow-50 hover:text-yellow-500"
												aria-label={`Set ${item.name} as default`}
												data-testid={`set-default-${index}`}
											>
												<FiStar size={16} />
											</button>
										)}
									</td>
									<td className="px-4 py-3">
										{item.isDefault && (
											<Tooltip label={`This resume is currently ${item.isResumePrivate ? "private" : "public"} and will be ${item.isResumePrivate ? "hidden" : "visible"}  on your profile. Click to make it ${item.isResumePrivate ? "public" : "private"}.`}>
												<button
													type="button"
													onClick={() => onChangePrivate()}
													className="rounded pl-2 hover:text-blue-500"
													aria-label={`Set ${item.name} as default`}
													data-testid={`set-default-${index}`}
												>
													{item.isResumePrivate ? <FiEyeOff size={16} /> : <FiEye size={16} />}
												</button>
											</Tooltip>
										)}
										{!item.isDefault && (
											<div className="pl-2">
												<FiEyeOff size={16} />
											</div>
										)}
									</td>
									<td className="px-4 py-3 text-gray-800"><a
										href={item.downloadUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="rounded p-1.5 hover:bg-blue-50 hover:text-blue-500"
										aria-label={`Download ${item.name}`}
										data-testid={`download-resume-${index}`}
										title="Download Resume"
									>{item.name}</a></td>
									<td className="px-4 py-3 text-gray-600">{item.size}</td>
									<td className="px-4 py-3 text-gray-600">{item.type}</td>
									<td className="px-4 py-3">
										<div className="flex items-center gap-2">
											{item.downloadUrl && (
												<a
													href={item.downloadUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="rounded p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-500"
													aria-label={`Download ${item.name}`}
													data-testid={`download-resume-${index}`}
													title="Download Resume"
												>
													<FiDownload size={16} />
												</a>
											)}
											<button
												type="button"
												onClick={() => onRemove(index)}
												className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
												aria-label={`Remove ${item.name}`}
												data-testid={`remove-resume-${index}`}
											>
												<FiTrash2 size={16} />
											</button>
										</div>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			<input
				ref={fileInputRef}
				type="file"
				accept=".pdf,.doc,.docx"
				onChange={handleFileChange}
				className="hidden"
				data-testid="resume-file-input"
			/>
			<button
				type="button"
				onClick={() => fileInputRef.current?.click()}
				className="mt-3 flex items-center gap-2 self-start rounded border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600"
				data-testid="upload-resume-btn"
			>
				<FiUpload size={16} />
				Upload Resume
			</button>
		</div >
	);
};
